import { FeedbackFormulaSchema, RuleSchema } from '@/types/schema';

export type CompositeScoreRule = RuleSchema & {
  formula: FeedbackFormulaSchema;
};

export function isCompositeScoreRule(
  rule: RuleSchema
): rule is CompositeScoreRule {
  return 'formula' in rule;
}

/**
 * Converts a FeedbackFormulaSchema to a RuleSchema for display in the EvaluatorsTable
 */
export function convertFeedbackFormulaToRule(
  formula: FeedbackFormulaSchema
): CompositeScoreRule {
  return {
    id: formula.id,
    is_enabled: true, // status: always true as specified
    display_name: formula.feedback_key, // display_name: feedback_key
    filter: '', // Not applicable for feedback formulas
    sampling_rate: 1, // Default value
    evaluator_version: 1, // Default value
    created_at: formula.created_at, // created_at: created_at
    updated_at: formula.modified_at, // updated_at: modified_at (note: formula uses modified_at)

    // Optional fields that don't apply to feedback formulas
    trace_filter: undefined,
    tree_filter: undefined,
    backfill_from: undefined,
    session_id: undefined,
    session_name: undefined,
    dataset_id: undefined,
    dataset_name: undefined,
    alerts: undefined,
    webhooks: undefined,
    add_to_annotation_queue_id: undefined,
    add_to_annotation_queue_name: undefined,
    add_to_dataset_id: undefined,
    add_to_dataset_name: undefined,
    use_corrections_dataset: undefined,
    num_few_shot_examples: undefined,
    corrections_dataset_id: undefined,
    extend_only: undefined,
    evaluators: undefined,
    code_evaluators: undefined,
    formula,
  };
}

/**
 * Converts an array of FeedbackFormulaSchema to RuleSchema for display
 */
function convertFeedbackFormulasToRules(
  formulas: FeedbackFormulaSchema[]
): CompositeScoreRule[] {
  return formulas.map(convertFeedbackFormulaToRule);
}

/**
 * Combines and sorts rules and formulas by updated_at
 */
export function combineAndSortRulesWithFormulas(
  rules: RuleSchema[],
  formulas: FeedbackFormulaSchema[] | null | undefined
): (RuleSchema | CompositeScoreRule)[] {
  const formulaRules = formulas ? convertFeedbackFormulasToRules(formulas) : [];
  const combined = [...rules, ...formulaRules];

  // Sort by updated_at in descending order (most recent first)
  return combined.sort(
    (a, b) =>
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  );
}
